/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidencAccession e any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { forwardRef, useEffect, useState } from "react";
import classNames from "classnames";

export type SwitchValue = boolean | null;

export type BootstrapSwitchProps = {
  value?: SwitchValue;
  defaultValue?: SwitchValue;

  disabled?: boolean;
  readonly?: boolean;
  animate?: boolean;
  tristate?: boolean;
  baseClass?: string;

  onColor?:
    | "primary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "default"
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any;
  offColor?:
    | "primary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "default"
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any;

  onText?: React.ReactNode;
  offText?: React.ReactNode;
  labelText?: string;

  handleWidth?: number;

  onChange?: (
    elm: HTMLInputElement | HTMLBaseElement,
    state: SwitchValue
  ) => void;

  id?: string;
  name?: string;
};

const cycleTristate = (value: SwitchValue): SwitchValue => {
  if (value === true) return false;
  if (value === false) return null;
  return true;
};

const BootstrapSwitch = forwardRef<HTMLInputElement, BootstrapSwitchProps>(
  (
    {
      value,
      defaultValue = false,

      disabled = false,
      readonly = false,
      animate = true,
      tristate = false,
      baseClass = "",
      onColor = "primary",
      offColor = "default",

      onText = "Enabled",
      offText = "Disabled",
      labelText = "",

      handleWidth = 75,

      onChange,
      id,
      name,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState<SwitchValue>(
      value ?? defaultValue
    );

    const isControlled = value !== undefined;
    const current = isControlled ? value : internalValue;

    useEffect(() => {
      if (isControlled) setInternalValue(value);
    }, [value, isControlled]);

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled || readonly) return;

      let next: SwitchValue;

      if (tristate) {
        next = cycleTristate(current);
      } else {
        next = !current;
      }

      if (!isControlled) {
        setInternalValue(next);
      }

      onChange?.(e.target, next);
    };

    const stateClass =
      current === true
        ? "bootstrap-switch-on"
        : current === false
        ? "bootstrap-switch-off"
        : "bootstrap-switch-null";
    const containerWidth = handleWidth * 3;

    return (
      <label
        className={classNames("bootstrap-switch", baseClass, stateClass, {
          "bootstrap-switch-animate": animate,
          "bootstrap-switch-disabled": disabled,
          "bootstrap-switch-readonly": readonly,
        })}
        style={{ width: Math.abs((containerWidth * 2) / 3) }}
      >
        <input
          ref={ref}
          type="checkbox"
          id={id}
          name={name}
          checked={!!current}
          disabled={disabled || readonly}
          onChange={handleToggle}
        />

        <div
          className="bootstrap-switch-container"
          style={{ width: containerWidth }}
        >
          <span
            className={`bootstrap-switch-handle-on bootstrap-switch-${onColor} ${
              !current ? "visibility-hidden" : ""
            }`}
            style={{ width: handleWidth }}
          >
            {onText}
          </span>

          <span
            className="bootstrap-switch-label"
            style={{ width: handleWidth }}
          >
            {labelText || "\u00A0"}
          </span>

          <span
            className={`bootstrap-switch-handle-off bootstrap-switch-${offColor} ${
              current ? "visibility-hidden" : ""
            }`}
            style={{ width: handleWidth }}
          >
            {offText}
          </span>
        </div>
      </label>
    );
  }
);

BootstrapSwitch.displayName = "BootstrapSwitch";

export default BootstrapSwitch;
